import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import toast from 'react-hot-toast';

interface User {
  id: string;
  nome: string;
  email: string;
  role: string;
  ativo: boolean;
  tenantId?: string;
  ultimoLogin?: string | null;
  criadoEm: string;
}

interface AuthSession {
  id: string;
  token: string;
  user: User;
  lastUsedAt: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, senha: string) => Promise<boolean>;
  logout: () => void;
  logoutAll: () => void;
  checkAuth: () => Promise<boolean>;
  sessions: AuthSession[];
  activeSessionId: string | null;
  switchSession: (sessionId: string) => Promise<boolean>;
  removeSession: (sessionId: string) => void;
  setUser: (user: User | null) => void;
  setToken: (token: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const API_BASE_URL = '/api';
const AUTH_SESSIONS_KEY = 'auth_sessions';
const ACTIVE_SESSION_KEY = 'active_auth_session_id';

function buildSessionId(user: User) {
  return user.id;
}

function getSessionAccountKey(session: AuthSession) {
  return session.user.id || session.user.email || session.id;
}

function normalizeSessions(sessions: AuthSession[]) {
  const seen = new Set<string>();
  const normalized: AuthSession[] = [];

  for (const session of sessions) {
    const canonicalId = buildSessionId(session.user);
    const accountKey = getSessionAccountKey(session);
    if (seen.has(accountKey)) continue;
    seen.add(accountKey);
    normalized.push({ ...session, id: canonicalId });
  }

  return normalized;
}

function readSessions(): AuthSession[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(AUTH_SESSIONS_KEY) || '[]');
    return Array.isArray(parsed) ? normalizeSessions(parsed) : [];
  } catch {
    return [];
  }
}

function writeSessions(sessions: AuthSession[]) {
  localStorage.setItem(AUTH_SESSIONS_KEY, JSON.stringify(normalizeSessions(sessions)));
}

function clearTenantSelection() {
  localStorage.removeItem('selected_tenant_id');
  localStorage.removeItem('superadmin_selected_tenant');
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('auth_token'));
  const [sessions, setSessions] = useState<AuthSession[]>(() => readSessions());
  const [activeSessionId, setActiveSessionId] = useState<string | null>(() => localStorage.getItem(ACTIVE_SESSION_KEY));
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  // Interceptar requisições para adicionar token
  const apiRequest = async (url: string, options: RequestInit = {}): Promise<Response> => {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    return fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers,
    });
  };

  const login = async (email: string, senha: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, senha }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.message || 'Erro ao fazer login');
        return false;
      }

      const { token: newToken, user: userData } = data.data;
      const sessionId = buildSessionId(userData);
      const nextSession: AuthSession = {
        id: sessionId,
        token: newToken,
        user: userData,
        lastUsedAt: new Date().toISOString(),
      };
      const nextSessions = normalizeSessions([nextSession, ...readSessions().filter((session) => buildSessionId(session.user) !== sessionId)]);

      setToken(newToken);
      setUser(userData);
      setSessions(nextSessions);
      setActiveSessionId(sessionId);
      localStorage.setItem('auth_token', newToken);
      localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
      writeSessions(nextSessions);
      clearTenantSelection();

      toast.success('Login realizado com sucesso!');
      return true;
    } catch (error) {
      console.error('Erro no login:', error);
      toast.error('Erro de conexão. Tente novamente.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    const currentSessionId = activeSessionId;
    const nextSessions = readSessions().filter((session) => session.id !== currentSessionId);
    setSessions(nextSessions);
    writeSessions(nextSessions);

    const nextSession = nextSessions[0];
    if (nextSession) {
      setUser(nextSession.user);
      setToken(nextSession.token);
      setActiveSessionId(nextSession.id);
      localStorage.setItem('auth_token', nextSession.token);
      localStorage.setItem(ACTIVE_SESSION_KEY, nextSession.id);
      clearTenantSelection();
      toast.success('Conta removida. Outra sessão foi ativada.');
      window.location.reload();
      return;
    }

    setUser(null);
    setToken(null);
    setActiveSessionId(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem(ACTIVE_SESSION_KEY);
    clearTenantSelection();
    toast.success('Logout realizado com sucesso!');
  };

  const logoutAll = () => {
    setUser(null);
    setToken(null);
    setSessions([]);
    setActiveSessionId(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem(AUTH_SESSIONS_KEY);
    localStorage.removeItem(ACTIVE_SESSION_KEY);
    clearTenantSelection();
    toast.success('Todas as contas foram desconectadas.');
  };

  const switchSession = async (sessionId: string): Promise<boolean> => {
    const targetSession = readSessions().find((session) => session.id === sessionId);
    if (!targetSession) {
      toast.error('Sessão não encontrada.');
      return false;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/auth/verify`, {
        headers: { Authorization: `Bearer ${targetSession.token}` },
      });

      if (!response.ok) {
        const nextSessions = readSessions().filter((session) => session.id !== sessionId);
        setSessions(nextSessions);
        writeSessions(nextSessions);
        toast.error('Sessão expirada. Faça login novamente.');
        return false;
      }

      const data = await response.json();
      const verifiedUser = data.data.user;
      const updatedSession: AuthSession = {
        ...targetSession,
        user: verifiedUser,
        lastUsedAt: new Date().toISOString(),
      };
      const canonicalSessionId = buildSessionId(verifiedUser);
      const nextSessions = normalizeSessions([
        { ...updatedSession, id: canonicalSessionId },
        ...readSessions().filter((session) => buildSessionId(session.user) !== canonicalSessionId)
      ]);

      setUser(verifiedUser);
      setToken(targetSession.token);
      setSessions(nextSessions);
      setActiveSessionId(canonicalSessionId);
      localStorage.setItem('auth_token', targetSession.token);
      localStorage.setItem(ACTIVE_SESSION_KEY, canonicalSessionId);
      writeSessions(nextSessions);
      clearTenantSelection();
      toast.success('Conta alterada com sucesso.');
      window.location.reload();
      return true;
    } catch (error) {
      console.error('Erro ao trocar sessão:', error);
      toast.error('Erro ao trocar conta.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const removeSession = (sessionId: string) => {
    if (sessionId === activeSessionId) {
      logout();
      return;
    }
    const nextSessions = readSessions().filter((session) => session.id !== sessionId);
    setSessions(nextSessions);
    writeSessions(nextSessions);
    toast.success('Conta removida.');
  };

  const updateToken = (nextToken: string) => {
    setToken(nextToken);
    localStorage.setItem('auth_token', nextToken);
    if (activeSessionId) {
      const nextSessions = readSessions().map((session) =>
        session.id === activeSessionId ? { ...session, token: nextToken, lastUsedAt: new Date().toISOString() } : session
      );
      setSessions(nextSessions);
      writeSessions(nextSessions);
    }
  };

  const checkAuth = async (): Promise<boolean> => {
    const normalizedStoredSessions = readSessions();
    if (normalizedStoredSessions.length > 0) {
      setSessions(normalizedStoredSessions);
      writeSessions(normalizedStoredSessions);
      if (activeSessionId && !normalizedStoredSessions.some((session) => session.id === activeSessionId)) {
        const matchingActiveSession = normalizedStoredSessions.find((session) => token && session.token === token);
        if (matchingActiveSession) {
          setActiveSessionId(matchingActiveSession.id);
          localStorage.setItem(ACTIVE_SESSION_KEY, matchingActiveSession.id);
        }
      }
    }

    if (!token) {
      const activeStoredSession = normalizedStoredSessions.find((session) => session.id === activeSessionId) || normalizedStoredSessions[0];
      if (activeStoredSession) {
        setToken(activeStoredSession.token);
        setUser(activeStoredSession.user);
        setActiveSessionId(activeStoredSession.id);
        localStorage.setItem('auth_token', activeStoredSession.token);
        localStorage.setItem(ACTIVE_SESSION_KEY, activeStoredSession.id);
        setIsLoading(false);
        return true;
      }
      setIsLoading(false);
      return false;
    }

    try {
      const response = await apiRequest('/auth/verify');

      if (!response.ok) {
        logout();
        return false;
      }

      const data = await response.json();
      setUser(data.data.user);
      if (activeSessionId) {
        const nextSessions = readSessions().map((session) =>
          session.id === activeSessionId ? { ...session, user: data.data.user, lastUsedAt: new Date().toISOString() } : session
        );
        setSessions(nextSessions);
        writeSessions(nextSessions);
      } else {
        const sessionId = buildSessionId(data.data.user);
        const nextSession: AuthSession = {
          id: sessionId,
          token,
          user: data.data.user,
          lastUsedAt: new Date().toISOString(),
        };
        const nextSessions = normalizeSessions([nextSession, ...readSessions().filter((session) => buildSessionId(session.user) !== sessionId)]);
        setSessions(nextSessions);
        setActiveSessionId(sessionId);
        localStorage.setItem(ACTIVE_SESSION_KEY, sessionId);
        writeSessions(nextSessions);
      }
      return true;
    } catch (error) {
      console.error('Erro ao verificar autenticação:', error);
      logout();
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Interceptar respostas 401 da API para fazer logout automático
  useEffect(() => {
    const originalFetch = window.fetch;

    window.fetch = async (...args) => {
      const response = await originalFetch(...args);

      // Só faz logout se for uma resposta 401 de uma rota da API
      if (response.status === 401 && isAuthenticated && args[0]?.toString().includes('/api/')) {
        console.log('Token expirado ou inválido, fazendo logout...');
        logout();
      }

      return response;
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [isAuthenticated, logout]);

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated,
    isLoading,
    login,
    logout,
    logoutAll,
    checkAuth,
    sessions,
    activeSessionId,
    switchSession,
    removeSession,
    setUser,
    setToken: updateToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
